import { createContext, useContext, useState, ReactNode, useEffect } from 'react'
import { useAuth } from './AuthContext'

export type UserProfile = 'master' | 'shipper' | 'freight-forwarder'

interface ProfileContextType {
  currentProfile: UserProfile
  setProfile: (profile: UserProfile) => void
  canAccessProfile: (profile: UserProfile) => boolean
}

const ProfileContext = createContext<ProfileContextType | undefined>(undefined)

export function ProfileProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()

  // Inicializar baseado no user atual
  const getInitialProfile = (): UserProfile => {
    if (user?.user_master) return 'master'

    // Se tem current_company, usa o tipo dela
    if (user?.current_company?.type === 'freight-forwarder') return 'freight-forwarder'
    if (user?.current_company?.type === 'shipper') return 'shipper'

    // Se não tem current_company, verifica as empresas disponíveis
    // Prioriza shipper se tiver, senão freight-forwarder
    const hasShipper = user?.companies?.some(c => c.type === 'shipper')
    const hasFreightForwarder = user?.companies?.some(c => c.type === 'freight-forwarder')

    if (hasShipper) return 'shipper'
    if (hasFreightForwarder) return 'freight-forwarder'

    // Fallback - não deveria chegar aqui se o usuário tem empresas
    return 'shipper'
  }

  const [currentProfile, setCurrentProfile] = useState<UserProfile>(getInitialProfile)

  // Atualizar quando o user mudar (login, logout, troca de company)
  useEffect(() => {
    setCurrentProfile(getInitialProfile())
  }, [user])

  const canAccessProfile = (profile: UserProfile): boolean => {
    if (!user) return false

    // Em dev mode, masters podem acessar todos os perfis para testes
    const isDevMode = import.meta.env.DEV || !import.meta.env.PROD
    if (isDevMode && user.user_master) {
      return true // Masters em dev podem ver tudo
    }

    if (profile === 'master') {
      return user.user_master === true
    }

    if (profile === 'shipper') {
      return user.companies?.some(c => c.type === 'shipper') || false
    }

    if (profile === 'freight-forwarder') {
      return user.companies?.some(c => c.type === 'freight-forwarder') || false
    }

    return false
  }

  const setProfile = (profile: UserProfile) => {
    if (!canAccessProfile(profile)) {
      console.warn(`User cannot access profile: ${profile}`)
      return
    }
    setCurrentProfile(profile)
  }

  return (
    <ProfileContext.Provider value={{ currentProfile, setProfile, canAccessProfile }}>
      {children}
    </ProfileContext.Provider>
  )
}

export function useProfile() {
  const context = useContext(ProfileContext)
  if (context === undefined) {
    throw new Error('useProfile must be used within a ProfileProvider')
  }
  return context
}
